/**
 * useCalls Hook
 *
 * Manages video/audio calls using WebRTC media streams.
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import type { DAGObject, KeyPair, CallOfferContent, CallAnswerContent, CallEndContent, CallSignal } from '@amorphous/core';
import { createCallOffer, createCallAnswer, createCallEnd, createDAGObject, signObject, isCallOffer, isCallAnswer, isCallEnd } from '@amorphous/core';

// ICE server configuration
const ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

export type CallStatus = 'idle' | 'calling' | 'ringing' | 'active' | 'ended';

export interface ActiveCall {
  callId: string;
  peerPubkey: string;
  isOutgoing: boolean;
  video: boolean;
  audio: boolean;
  status: CallStatus;
  startTime?: number;
  localStream?: MediaStream;
  remoteStream?: MediaStream;
}

export interface IncomingCall {
  callId: string;
  callerPubkey: string;
  video: boolean;
  audio: boolean;
  offerHash: string;
}

interface UseCallsConfig {
  identity: { keyPair: KeyPair } | null;
  dagStore: {
    put: (obj: DAGObject) => Promise<string>;
    get: (hash: string) => Promise<DAGObject | null>;
    getAll: () => Promise<DAGObject[]>;
  } | null;
  /** Function to send call signals (SDP/ICE) to a peer via data channel */
  sendCallSignal?: (peerId: string, signal: CallSignal) => boolean;
  /** Function to broadcast DAG objects to peers */
  broadcastObject?: (obj: DAGObject) => void;
}

export function useCalls({
  identity,
  dagStore,
  sendCallSignal,
  broadcastObject,
}: UseCallsConfig) {
  const [activeCall, setActiveCall] = useState<ActiveCall | null>(null);
  const [incomingCall, setIncomingCall] = useState<IncomingCall | null>(null);
  const [error, setError] = useState<string | null>(null);

  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const pendingIceCandidatesRef = useRef<Map<string, RTCIceCandidateInit[]>>(new Map());
  const activeCallRef = useRef<ActiveCall | null>(null);

  // Keep activeCallRef in sync with activeCall state
  useEffect(() => {
    activeCallRef.current = activeCall;
  }, [activeCall]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (pcRef.current) {
        pcRef.current.close();
      }
    };
  }, []);

  // Request media permissions
  const getMediaStream = useCallback(async (video: boolean, audio: boolean): Promise<MediaStream | null> => {
    try {
      const constraints: MediaStreamConstraints = {};
      if (video) constraints.video = true;
      if (audio) constraints.audio = true;

      if (!constraints.video && !constraints.audio) {
        constraints.audio = true; // At least audio
      }

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      localStreamRef.current = stream;
      return stream;
    } catch (err) {
      console.error('[Calls] Failed to get media stream:', err);
      setError('Failed to access camera/microphone');
      return null;
    }
  }, []);

  // Create WebRTC peer connection for a call
  const createCallConnection = useCallback((callId: string, peerPubkey: string): RTCPeerConnection => {
    console.log(`[Calls] Creating WebRTC connection for call ${callId.slice(0, 8)} with ${peerPubkey.slice(0, 8)}`);

    const config: RTCConfiguration = {
      iceServers: ICE_SERVERS,
    };

    const pc = new RTCPeerConnection(config);
    pcRef.current = pc;

    // Add local tracks to the connection
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        console.log(`[Calls] Adding local ${track.kind} track`);
        pc.addTrack(track, localStreamRef.current!);
      });
    }

    // Handle incoming remote tracks
    pc.ontrack = (event) => {
      console.log(`[Calls] Received remote ${event.track.kind} track`);
      const [remoteStream] = event.streams;
      if (remoteStream) {
        setActiveCall(prev => prev ? { ...prev, remoteStream } : null);
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = remoteStream;
        }
      }
    };

    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate && sendCallSignal) {
        console.log(`[Calls] Sending ICE candidate to ${peerPubkey.slice(0, 8)}`);
        sendCallSignal(peerPubkey, {
          type: 'call-ice',
          callId,
          payload: JSON.stringify(event.candidate.toJSON()),
        });
      }
    };

    // Monitor connection state
    pc.onconnectionstatechange = () => {
      console.log(`[Calls] Connection state: ${pc.connectionState}`);
      if (pc.connectionState === 'failed') {
        setError('Call connection failed');
        // Don't auto-end, let user decide
      } else if (pc.connectionState === 'disconnected') {
        console.log('[Calls] Connection temporarily disconnected, may recover...');
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log(`[Calls] ICE connection state: ${pc.iceConnectionState}`);
    };

    return pc;
  }, [sendCallSignal]);

  // Start an outgoing call
  const startCall = useCallback(async (
    recipientPubkey: string,
    options: { video?: boolean; audio?: boolean } = {}
  ): Promise<string | null> => {
    if (!identity || !dagStore) {
      setError('Not logged in');
      return null;
    }

    if (activeCall) {
      setError('Already in a call');
      return null;
    }

    const video = options.video ?? false;
    const audio = options.audio ?? true;

    // Get local media
    const localStream = await getMediaStream(video, audio);
    if (!localStream) return null;

    // Create call offer content
    const { type, content } = createCallOffer(recipientPubkey, { video, audio });

    // Create and sign the DAG object
    const obj = createDAGObject({
      type,
      content,
      creator: identity.keyPair.publicKeyBase58,
      timestamp: Date.now(),
      parents: [],
    });
    const signed = signObject(obj, identity.keyPair);

    // Store the offer
    await dagStore.put(signed);

    // Broadcast to peers so they receive the call
    if (broadcastObject) {
      console.log('[Calls] Broadcasting call offer to peers');
      broadcastObject(signed);
    }

    const callId = (content as CallOfferContent).callId;

    setActiveCall({
      callId,
      peerPubkey: recipientPubkey,
      isOutgoing: true,
      video,
      audio,
      status: 'calling',
      localStream,
    });

    // Display local preview
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }

    return callId;
  }, [identity, dagStore, activeCall, getMediaStream]);

  // Accept an incoming call
  const acceptCall = useCallback(async (): Promise<boolean> => {
    if (!identity || !dagStore || !incomingCall) {
      return false;
    }

    // Get local media
    const localStream = await getMediaStream(incomingCall.video, incomingCall.audio);
    if (!localStream) return false;

    // Create call answer
    const { type, content } = createCallAnswer(incomingCall.callId, true);

    const obj = createDAGObject({
      type,
      content,
      creator: identity.keyPair.publicKeyBase58,
      timestamp: Date.now(),
      parents: [],
    });
    const signed = signObject(obj, identity.keyPair);
    await dagStore.put(signed);

    // Broadcast answer to peers
    if (broadcastObject) {
      console.log('[Calls] Broadcasting call answer to peers');
      broadcastObject(signed);
    }

    setActiveCall({
      callId: incomingCall.callId,
      peerPubkey: incomingCall.callerPubkey,
      isOutgoing: false,
      video: incomingCall.video,
      audio: incomingCall.audio,
      status: 'active',
      startTime: Date.now(),
      localStream,
    });

    setIncomingCall(null);

    // Display local preview
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }

    return true;
  }, [identity, dagStore, incomingCall, getMediaStream, broadcastObject]);

  // Decline an incoming call
  const declineCall = useCallback(async (reason?: string): Promise<void> => {
    if (!identity || !dagStore || !incomingCall) return;

    const { type, content } = createCallAnswer(incomingCall.callId, false, reason || 'declined');

    const obj = createDAGObject({
      type,
      content,
      creator: identity.keyPair.publicKeyBase58,
      timestamp: Date.now(),
      parents: [],
    });
    const signed = signObject(obj, identity.keyPair);
    await dagStore.put(signed);

    // Broadcast decline to peers
    if (broadcastObject) {
      broadcastObject(signed);
    }

    setIncomingCall(null);
  }, [identity, dagStore, incomingCall, broadcastObject]);

  // End an active call
  const endCall = useCallback(async (): Promise<void> => {
    if (!identity || !dagStore || !activeCall) return;

    const duration = activeCall.startTime
      ? Math.floor((Date.now() - activeCall.startTime) / 1000)
      : 0;

    const { type, content } = createCallEnd(activeCall.callId, duration, 'hangup');

    const obj = createDAGObject({
      type,
      content,
      creator: identity.keyPair.publicKeyBase58,
      timestamp: Date.now(),
      parents: [],
    });
    const signed = signObject(obj, identity.keyPair);
    await dagStore.put(signed);

    // Broadcast end to peers
    if (broadcastObject) {
      broadcastObject(signed);
    }

    // Stop media tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }

    // Close peer connection
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }

    setActiveCall(null);
  }, [identity, dagStore, activeCall, broadcastObject]);

  // Toggle video
  const toggleVideo = useCallback(() => {
    if (localStreamRef.current) {
      const videoTracks = localStreamRef.current.getVideoTracks();
      videoTracks.forEach(track => {
        track.enabled = !track.enabled;
      });
      setActiveCall(prev => prev ? { ...prev, video: videoTracks[0]?.enabled ?? false } : null);
    }
  }, []);

  // Toggle audio (mute/unmute)
  const toggleAudio = useCallback(() => {
    if (localStreamRef.current) {
      const audioTracks = localStreamRef.current.getAudioTracks();
      audioTracks.forEach(track => {
        track.enabled = !track.enabled;
      });
      setActiveCall(prev => prev ? { ...prev, audio: audioTracks[0]?.enabled ?? false } : null);
    }
  }, []);

  // Process incoming call objects
  const handleCallObject = useCallback(async (obj: DAGObject) => {
    if (!identity) return;

    const myPubkey = identity.keyPair.publicKeyBase58;

    if (isCallOffer(obj)) {
      const content = obj.content as CallOfferContent;
      // Is this call for me?
      if (content.recipient === myPubkey && obj.creator !== myPubkey) {
        // Don't show if we're already in a call
        if (!activeCallRef.current) {
          setIncomingCall({
            callId: content.callId,
            callerPubkey: obj.creator,
            video: content.video,
            audio: content.audio,
            offerHash: obj.hash,
          });
        }
      }
    } else if (isCallAnswer(obj)) {
      const content = obj.content as CallAnswerContent;
      // Is this an answer to our call?
      const currentCall = activeCallRef.current;
      if (currentCall && currentCall.callId === content.callId && currentCall.isOutgoing) {
        if (content.accepted) {
          console.log(`[Calls] Call ${content.callId.slice(0, 8)} accepted, initiating WebRTC connection`);

          // Create WebRTC connection and send offer
          const pc = createCallConnection(content.callId, obj.creator);

          try {
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);

            if (sendCallSignal) {
              console.log(`[Calls] Sending SDP offer to ${obj.creator.slice(0, 8)}`);
              sendCallSignal(obj.creator, {
                type: 'call-sdp-offer',
                callId: content.callId,
                payload: JSON.stringify(offer),
              });
            }
          } catch (err) {
            console.error('[Calls] Failed to create offer:', err);
            setError('Failed to establish call connection');
          }

          setActiveCall(prev => prev ? {
            ...prev,
            status: 'active',
            startTime: Date.now(),
          } : null);
        } else {
          // Call was declined
          if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach(track => track.stop());
            localStreamRef.current = null;
          }
          setActiveCall(null);
          setError(`Call declined: ${content.declineReason || 'No reason given'}`);
        }
      }
    } else if (isCallEnd(obj)) {
      const content = obj.content as CallEndContent;
      // Is this ending our call?
      const currentCall = activeCallRef.current;
      if (currentCall && currentCall.callId === content.callId) {
        if (localStreamRef.current) {
          localStreamRef.current.getTracks().forEach(track => track.stop());
          localStreamRef.current = null;
        }
        if (pcRef.current) {
          pcRef.current.close();
          pcRef.current = null;
        }
        setActiveCall(null);
      }
      // Also clear incoming if it matches
      if (incomingCall && incomingCall.callId === content.callId) {
        setIncomingCall(null);
      }
    }
  }, [identity, incomingCall, createCallConnection, sendCallSignal]);

  // Set video element refs
  const setLocalVideoRef = useCallback((el: HTMLVideoElement | null) => {
    localVideoRef.current = el;
    if (el && localStreamRef.current) {
      el.srcObject = localStreamRef.current;
    }
  }, []);

  const setRemoteVideoRef = useCallback((el: HTMLVideoElement | null) => {
    remoteVideoRef.current = el;
    // If we already have a remote stream, attach it
    if (el && activeCall?.remoteStream) {
      el.srcObject = activeCall.remoteStream;
    }
  }, [activeCall?.remoteStream]);

  // Handle incoming call signals (SDP offers, answers, and ICE candidates)
  const handleCallSignal = useCallback(async (fromPeer: string, signal: CallSignal) => {
    const currentCall = activeCallRef.current;

    if (!currentCall || currentCall.callId !== signal.callId) {
      console.warn(`[Calls] Received signal for unknown/mismatched call ${signal.callId.slice(0, 8)}`);
      return;
    }

    console.log(`[Calls] Handling ${signal.type} from ${fromPeer.slice(0, 8)}`);

    switch (signal.type) {
      case 'call-sdp-offer': {
        // We're the callee receiving the WebRTC offer
        const offer: RTCSessionDescriptionInit = JSON.parse(signal.payload);

        let pc = pcRef.current;
        if (!pc) {
          pc = createCallConnection(signal.callId, fromPeer);
        }

        try {
          await pc.setRemoteDescription(new RTCSessionDescription(offer));

          // Apply any buffered ICE candidates
          const buffered = pendingIceCandidatesRef.current.get(signal.callId) || [];
          for (const candidate of buffered) {
            await pc.addIceCandidate(new RTCIceCandidate(candidate));
          }
          pendingIceCandidatesRef.current.delete(signal.callId);

          // Create and send answer
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);

          if (sendCallSignal) {
            console.log(`[Calls] Sending SDP answer to ${fromPeer.slice(0, 8)}`);
            sendCallSignal(fromPeer, {
              type: 'call-sdp-answer',
              callId: signal.callId,
              payload: JSON.stringify(answer),
            });
          }
        } catch (err) {
          console.error('[Calls] Failed to handle offer:', err);
          setError('Failed to establish call connection');
        }
        break;
      }

      case 'call-sdp-answer': {
        // We're the caller receiving the WebRTC answer
        const answer: RTCSessionDescriptionInit = JSON.parse(signal.payload);
        const pc = pcRef.current;

        if (!pc) {
          console.warn('[Calls] Received answer but no peer connection exists');
          return;
        }

        try {
          await pc.setRemoteDescription(new RTCSessionDescription(answer));

          // Apply any buffered ICE candidates
          const buffered = pendingIceCandidatesRef.current.get(signal.callId) || [];
          for (const candidate of buffered) {
            await pc.addIceCandidate(new RTCIceCandidate(candidate));
          }
          pendingIceCandidatesRef.current.delete(signal.callId);

          console.log('[Calls] WebRTC connection established');
        } catch (err) {
          console.error('[Calls] Failed to handle answer:', err);
          setError('Failed to establish call connection');
        }
        break;
      }

      case 'call-ice': {
        // ICE candidate from peer
        const candidate: RTCIceCandidateInit = JSON.parse(signal.payload);
        const pc = pcRef.current;

        if (pc && pc.remoteDescription) {
          // Remote description is set, add candidate directly
          try {
            await pc.addIceCandidate(new RTCIceCandidate(candidate));
          } catch (err) {
            console.warn('[Calls] Failed to add ICE candidate:', err);
          }
        } else {
          // Buffer the candidate until remote description is set
          console.log(`[Calls] Buffering ICE candidate for call ${signal.callId.slice(0, 8)}`);
          const buffered = pendingIceCandidatesRef.current.get(signal.callId) || [];
          buffered.push(candidate);
          pendingIceCandidatesRef.current.set(signal.callId, buffered);
        }
        break;
      }
    }
  }, [createCallConnection, sendCallSignal]);

  return {
    // State
    activeCall,
    incomingCall,
    error,

    // Actions
    startCall,
    acceptCall,
    declineCall,
    endCall,
    toggleVideo,
    toggleAudio,
    handleCallObject,
    handleCallSignal,

    // Refs
    setLocalVideoRef,
    setRemoteVideoRef,

    // Clear error
    clearError: () => setError(null),
  };
}
